import { SalesLeaderboard } from "@/components/SalesLeaderboard";
import { MySalesBonusWallet } from "@/components/SalesCommissionWallets";
import { AgreementPdfButton } from "@/components/AgreementPdfButton";
import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useSearch } from "wouter";
import QRCode from "qrcode";

// Mobile-first Sales & Merchant Onboarding portal. Self-contained (fetch + state)
// so it has no coupling to the customer app's data layer. All calls hit the
// committed /api/sales/* and /api/shop/terms/* endpoints. No mock data.

const token = () => (typeof window !== "undefined" ? localStorage.getItem("ayrshire_token") : null);
async function api(path: string, opts: RequestInit = {}) {
  const r = await fetch(`/api${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(token() ? { Authorization: `Bearer ${token()}` } : {}), ...(opts.headers || {}) },
  });
  const txt = await r.text();
  const data = txt ? JSON.parse(txt) : {};
  if (!r.ok) throw new Error(data.error || `Error ${r.status}`);
  return data;
}

/** Touch/mouse signature pad -> PNG data URL. */
function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const pos = (e: React.PointerEvent) => {
    const c = ref.current!; const r = c.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * c.width, y: ((e.clientY - r.top) / r.height) * c.height };
  };
  return (
    <div>
      <canvas ref={ref} width={600} height={180}
        className="w-full h-[180px] border-2 border-slate-300 rounded-xl bg-white touch-none select-none"
        style={{ touchAction: "none", overscrollBehavior: "none", userSelect: "none", WebkitUserSelect: "none" }}
        aria-label="Signature drawing area"
        onPointerDown={(e) => { e.preventDefault(); drawing.current = true; const c = ref.current!; const ctx = c.getContext("2d")!; ctx.lineWidth = 2.4; ctx.lineCap = "round"; ctx.strokeStyle = "#14263f"; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); (e.target as Element).setPointerCapture(e.pointerId); }}
        onPointerMove={(e) => { if (!drawing.current) return; e.preventDefault(); const ctx = ref.current!.getContext("2d")!; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); dirty.current = true; }}
        onPointerUp={(e) => { e.preventDefault(); drawing.current = false; if (dirty.current) onChange(ref.current!.toDataURL("image/png")); }}
        onPointerCancel={() => { drawing.current = false; if (dirty.current) onChange(ref.current!.toDataURL("image/png")); }}
        onLostPointerCapture={() => { drawing.current = false; }}
      />
      <button type="button" className="text-xs text-slate-500 mt-1 underline" onClick={() => { const c = ref.current!; c.getContext("2d")!.clearRect(0, 0, c.width, c.height); dirty.current = false; onChange(null); }}>Clear signature</button>
    </div>
  );
}

const btn = "rounded-xl font-semibold px-4 py-3 text-base w-full";
const field = "w-full border border-slate-300 rounded-xl px-3 py-3 text-base";

export default function SalesPortal() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const [me, setMe] = useState<{ role: string; name: string; forcePasswordChange?: boolean; territories?: string[] } | null>(null);
  const [gate, setGate] = useState<"loading" | "ok" | "denied" | "login">("loading");
  const [view, setView] = useState<{ name: "dashboard" } | { name: "wallet" } | { name: "leaderboard" } | { name: "register" } | { name: "merchant"; storeId: number } | { name: "leads" } | { name: "lead"; id: number }>(() => ({ name: new URLSearchParams(window.location.search).get("view") === "leaderboard" ? "leaderboard" : "dashboard" }));

  useEffect(() => { if (new URLSearchParams(search).get("view") === "leaderboard") setView({ name: "leaderboard" }); }, [search]);

  useEffect(() => {
    if (!token()) { setGate("login"); return; }
    api("/sales/me").then((d) => {
      if (!d || (d.role !== "sales" && d.role !== "admin")) { setGate("denied"); return; }
      setMe(d); setGate("ok");
    }).catch((e) => { setGate(e.message?.includes("401") || e.message?.includes("Invalid") ? "login" : "denied"); });
  }, []);

  if (gate === "loading") return <Center>Loading…</Center>;
  if (gate === "login") return <Center><p className="mb-3">Please log in to use the sales portal.</p><a href="/login" className={`${btn} bg-[#14263f] text-white inline-block`}>Log in</a></Center>;
  if (gate === "denied") return <Center>This area is for Ayrshire Hub sales staff only.</Center>;
  if (me?.forcePasswordChange) return <ChangePassword onDone={() => setMe({ ...me, forcePasswordChange: false })} />;

  return (
    <div className={`${view.name === "leaderboard" ? "max-w-4xl" : "max-w-xl"} mx-auto min-h-screen bg-slate-50`}>
      <header className="bg-[#14263f] text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div><div className="font-bold text-lg">Ayrshire Hub Sales</div><div className="text-xs text-white/70">{me?.name}{me?.territories?.length ? ` · ${me.territories.join(", ")}` : ""}</div></div>
        {view.name !== "dashboard" && <button onClick={() => setView({ name: "dashboard" })} className="text-sm underline">Dashboard</button>}
      </header>
      <main className="p-4 pb-24">
        {view.name === "dashboard" && <Dashboard onLeaderboard={() => setView({ name: "leaderboard" })} onWallet={() => setView({ name: "wallet" })} onRegister={() => setView({ name: "register" })} onOpen={(storeId) => setView({ name: "merchant", storeId })} onLeads={() => setView({ name: "leads" })} />}
        {view.name === "wallet" && <MySalesBonusWallet />}
        {view.name === "leaderboard" && <SalesLeaderboard />}
        {view.name === "register" && <RegisterWizard onCreated={(storeId) => setView({ name: "merchant", storeId })} onCancel={() => setView({ name: "dashboard" })} onSavedLead={() => setView({ name: "leads" })} />}
        {view.name === "merchant" && <MerchantDetail storeId={view.storeId} />}
        {view.name === "leads" && <LeadsList onOpen={(id) => setView({ name: "lead", id })} onRegister={() => setView({ name: "register" })} />}
        {view.name === "lead" && <LeadDetail id={view.id} onConverted={(storeId) => setView({ name: "merchant", storeId })} onBack={() => setView({ name: "leads" })} />}
      </main>
    </div>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen flex items-center justify-center text-center p-6 text-slate-600"><div>{children}</div></div>;
}

function ChangePassword({ onDone }: { onDone: () => void }) {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [err, setErr] = useState<string | null>(null); const [busy, setBusy] = useState(false);
  return (
    <Center><div className="w-full max-w-sm text-left">
      <h2 className="font-bold text-xl mb-1">Set a new password</h2>
      <p className="text-sm text-slate-500 mb-4">For security you must change your temporary password before continuing.</p>
      {err && <p className="text-red-600 text-sm mb-2">{err}</p>}
      <input className={`${field} mb-2`} type="password" placeholder="Current (temporary) password" value={cur} onChange={(e) => setCur(e.target.value)} />
      <input className={`${field} mb-3`} type="password" placeholder="New password (min 8 chars)" value={nw} onChange={(e) => setNw(e.target.value)} />
      <button className={`${btn} bg-[#14263f] text-white`} disabled={busy} onClick={async () => { setBusy(true); setErr(null); try { await api("/auth/change-password", { method: "POST", body: JSON.stringify({ currentPassword: cur, newPassword: nw }) }); onDone(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); } }}>{busy ? "Saving…" : "Save & continue"}</button>
    </div></Center>
  );
}

function Dashboard({ onLeaderboard, onWallet, onRegister, onOpen, onLeads }: { onLeaderboard: () => void; onWallet: () => void; onRegister: () => void; onOpen: (storeId: number) => void; onLeads: () => void }) {
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoError, setDemoError] = useState<string | null>(null);
  const [stats, setStats] = useState<Record<string, number> | null>(null);
  const [merchants, setMerchants] = useState<Array<Record<string, unknown>>>([]);
  useEffect(() => { void api("/sales/dashboard").then(setStats).catch(() => {}); void api("/sales/merchants").then((d) => setMerchants(d.items || [])).catch(() => {}); }, []);
  const labels = { totalLeads: "Leads", shopsRegistered: "Registered", stripePending: "Stripe pending", contractsAwaiting: "Contracts pending", menusAwaiting: "Menus pending", awaitingApproval: "Awaiting approval", activated: "Activated", followUpsDueToday: "Follow-ups due" } as const;
  return (
    <div>
      <button onClick={onLeaderboard} className="w-full rounded-2xl bg-gradient-to-br from-[#14263f] to-[#28476d] text-white p-5 mb-3 text-left"><span className="text-xs uppercase tracking-widest text-[#efc66b] font-bold">The sales leaderboard</span><span className="block text-xl font-black mt-2">Make your next move →</span><span className="block text-sm text-white/70 mt-1">Your rank, the team standings and the latest signups</span></button>
      <button onClick={onWallet} className="w-full rounded-2xl bg-[#14263f] text-white p-4 mb-4 text-left"><span className="block font-bold">My signup bonuses</span><span className="block text-sm text-white/70 mt-1">View your wallet, earnings and payment history →</span></button>
      <button disabled={demoBusy} onClick={async () => {
        setDemoBusy(true); setDemoError(null);
        try { const d = await api("/sales/demo/kitchen", { method: "POST" }); window.location.assign(`/business/order-pad?storeId=${d.storeId}`); }
        catch (e) { setDemoError((e as Error).message); setDemoBusy(false); }
      }} className={`${btn} bg-[#14263f] text-white mb-2`}>{demoBusy ? "Opening…" : "Demo order pad"}</button>
      <p className="text-sm text-slate-500 mb-3">Open the test kitchen and send a practice order. No payment or food required.</p>
      {demoError && <p role="alert" className="text-red-600 mb-3">{demoError}</p>}
      <button onClick={onRegister} className={`${btn} bg-emerald-600 text-white mb-2 text-lg py-4`}>+ Register New Shop</button>
      <button onClick={onLeads} className={`${btn} bg-white border border-[#14263f] text-[#14263f] mb-4`}>View leads / prospects</button>
      <div className="grid grid-cols-2 gap-2 mb-5">
        {Object.entries(labels).map(([k, label]) => (
          <div key={k} className="bg-white border border-slate-200 rounded-xl p-3">
            <div className="text-2xl font-black text-[#14263f]">{stats ? (stats[k] ?? 0) : "–"}</div>
            <div className="text-xs text-slate-500">{label}</div>
          </div>
        ))}
      </div>
      <h3 className="font-bold text-slate-700 mb-2">My merchants</h3>
      {merchants.length === 0 && <p className="text-sm text-slate-400">No merchants registered yet.</p>}
      <div className="space-y-2">
        {merchants.map((m) => (
          <button key={String(m.storeId)} onClick={() => onOpen(Number(m.storeId))} className="w-full text-left bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between">
            <div><div className="font-semibold">{String(m.name)}</div><div className="text-xs text-slate-500">{String(m.town)} · {String(m.stage)}</div></div>
            <div className="flex gap-1">
              {m.stripeComplete ? <Dot c="bg-emerald-500" /> : <Dot c="bg-amber-400" />}
              {m.isActive ? <span className="text-xs text-emerald-600 font-semibold">LIVE</span> : m.approved ? <span className="text-xs text-blue-600">approved</span> : null}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
const Dot = ({ c }: { c: string }) => <span className={`w-2.5 h-2.5 rounded-full ${c} inline-block`} />;

const STAGE_COLOR: Record<string, string> = { lead: "bg-slate-100 text-slate-600", contacted: "bg-blue-100 text-blue-700", interested: "bg-emerald-100 text-emerald-700", declined: "bg-red-100 text-red-600" };

function LeadsList({ onOpen, onRegister }: { onOpen: (id: number) => void; onRegister: () => void }) {
  const [leads, setLeads] = useState<Array<Record<string, unknown>> | null>(null);
  useEffect(() => { void api("/sales/leads").then((d) => setLeads(d.items || [])).catch(() => setLeads([])); }, []);
  return (
    <div>
      <h2 className="font-bold text-lg mb-3">Leads &amp; prospects</h2>
      <button onClick={onRegister} className={`${btn} bg-emerald-600 text-white mb-4`}>+ New shop (full signup)</button>
      {leads === null && <p className="text-sm text-slate-400">Loading…</p>}
      {leads && leads.length === 0 && <p className="text-sm text-slate-400">No leads yet. Start a signup and tap “Save as lead” to park a half-filled one here.</p>}
      <div className="space-y-2">
        {(leads || []).map((l) => (
          <button key={String(l["id"])} onClick={() => onOpen(Number(l["id"]))} className="w-full text-left bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between">
            <div><div className="font-semibold">{String(l["leadBusinessName"] || "Unnamed lead")}</div><div className="text-xs text-slate-500">{String(l["leadTown"] || "—")}{l["leadContactName"] ? ` · ${String(l["leadContactName"])}` : ""}</div></div>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STAGE_COLOR[String(l["stage"])] || "bg-slate-100 text-slate-600"}`}>{String(l["stage"])}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function LeadDetail({ id, onConverted, onBack }: { id: number; onConverted: (storeId: number) => void; onBack: () => void }) {
  const [lead, setLead] = useState<Record<string, unknown> | null>(null);
  const [notes, setNotes] = useState<Array<Record<string, unknown>>>([]);
  const [noteText, setNoteText] = useState(""); const [noteKind, setNoteKind] = useState("note");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => { void api(`/sales/leads/${id}`).then((d) => { setLead(d.lead); setNotes(d.notes || []); }).catch((e) => setErr(e.message)); }, [id]);
  useEffect(() => { load(); }, [load]);
  const patch = async (p: Record<string, unknown>) => { setBusy(true); setErr(null); try { await api(`/sales/leads/${id}`, { method: "PATCH", body: JSON.stringify(p) }); load(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); } };
  const addNote = async () => { if (!noteText.trim() && noteKind === "note") return; setBusy(true); setErr(null); try { await api("/sales/followups", { method: "POST", body: JSON.stringify({ onboardingId: id, kind: noteKind, note: noteText }) }); setNoteText(""); load(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); } };
  const convert = async () => { if (!confirm("Convert this lead into a live shop? This creates the shop + owner account and starts Stripe.")) return; setBusy(true); setErr(null); try { const d = await api(`/sales/leads/${id}/convert`, { method: "POST", body: JSON.stringify({}) }); onConverted(Number(d.storeId)); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); } };
  if (!lead) return <p className="text-sm text-slate-400">{err || "Loading…"}</p>;
  const STAGES = ["lead", "contacted", "interested", "declined"];
  const KINDS = ["note", "visited", "interested", "not_interested", "follow_up", "unavailable"];
  const g = (k: string) => (lead[k] ? String(lead[k]) : "");
  return (
    <div>
      <h2 className="font-bold text-lg mb-1">{g("leadBusinessName") || "Unnamed lead"}</h2>
      <p className="text-xs text-slate-500 mb-3">{[g("leadTown"), g("leadContactName"), g("leadPhone"), g("leadEmail")].filter(Boolean).join(" · ") || "No details yet"}</p>
      {err && <p className="text-red-600 text-sm mb-2">{err}</p>}
      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-3">
        <span className="text-sm text-slate-600 block mb-2">Status</span>
        <div className="grid grid-cols-4 gap-1">
          {STAGES.map((s) => <button key={s} disabled={busy} onClick={() => void patch({ stage: s })} className={`text-xs font-semibold rounded-lg py-2 ${lead["stage"] === s ? "bg-[#14263f] text-white" : "bg-slate-100 text-slate-600"}`}>{s}</button>)}
        </div>
      </div>
      <button disabled={busy} onClick={convert} className={`${btn} bg-emerald-600 text-white mb-4`}>Convert to live shop →</button>
      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-3">
        <span className="text-sm text-slate-600 block mb-2">Add note / outcome</span>
        <select className={`${field} mb-2`} value={noteKind} onChange={(e) => setNoteKind(e.target.value)}>{KINDS.map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}</select>
        <textarea className={field} rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Note (optional for an outcome)…" />
        <button disabled={busy} onClick={addNote} className={`${btn} bg-[#14263f] text-white mt-2`}>Add to history</button>
      </div>
      <h3 className="font-semibold text-slate-700 mb-2 text-sm">History</h3>
      {notes.length === 0 && <p className="text-xs text-slate-400">No notes yet.</p>}
      <div className="space-y-2">
        {notes.map((n) => (
          <div key={String(n["id"])} className="bg-slate-50 border border-slate-200 rounded-lg p-2">
            <div className="text-xs font-semibold text-[#14263f]">{String(n["kind"]).replace(/_/g, " ")}</div>
            {n["note"] ? <div className="text-sm text-slate-700">{String(n["note"])}</div> : null}
            <div className="text-[10px] text-slate-400">{new Date(String(n["createdAt"])).toLocaleString("en-GB")}</div>
          </div>
        ))}
      </div>
      <button onClick={onBack} className="text-sm text-slate-500 underline mt-4">← Back to leads</button>
    </div>
  );
}

function RegisterWizard({ onCreated, onCancel, onSavedLead }: { onCreated: (storeId: number) => void; onCancel: () => void; onSavedLead: () => void }) {
  const [step, setStep] = useState(0);
  const [f, setF] = useState<Record<string, string | boolean>>({ tabletProvided: false });
  const set = (k: string, v: string | boolean) => setF((p) => ({ ...p, [k]: v }));
  const [err, setErr] = useState<string | null>(null); const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ stripeOnboardingLink: string; activationLink: string | null } | null>(null);
  const Txt = (k: string, label: string, props: Record<string, unknown> = {}) => (
    <label className="block mb-3"><span className="text-sm text-slate-600">{label}</span>
      <input className={field} value={String(f[k] ?? "")} onChange={(e) => set(k, e.target.value)} {...props} /></label>
  );
  async function submit() {
    setBusy(true); setErr(null);
    try { const d = await api("/sales/merchants", { method: "POST", body: JSON.stringify(f) }); setResult({ stripeOnboardingLink: d.stripeOnboardingLink, activationLink: d.activationLink }); setStep(3); if (d.storeId) sessionStorage.setItem("sales_last_store", String(d.storeId)); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  async function saveLead() {
    setBusy(true); setErr(null);
    try {
      await api("/sales/leads", { method: "POST", body: JSON.stringify({ businessName: f["tradingName"], contactName: f["ownerName"], email: f["ownerEmail"], phone: f["phone"], town: f["town"], postcode: f["postcode"], category: f["category"] }) });
      onSavedLead();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  if (result) return (
    <div>
      <h2 className="font-bold text-xl mb-2">Shop created ✓</h2>
      <p className="text-sm text-slate-600 mb-4">The shop is set up in draft (not visible to customers). Help the owner complete payment setup now.</p>
      <LinkCard label="Owner: complete payment setup (Stripe)" url={result.stripeOnboardingLink} colour="#f97316" />
      {result.activationLink && <LinkCard label="Owner: set their password" url={result.activationLink} colour="#14263f" />}
      <button className={`${btn} bg-[#14263f] text-white mt-4`} onClick={() => onCreated(Number(sessionStorage.getItem("sales_last_store")))}>Continue to onboarding checklist</button>
    </div>
  );
  return (
    <div>
      <div className="flex gap-1 mb-3">{[0, 1, 2].map((s) => <div key={s} className={`h-1.5 flex-1 rounded ${s <= step ? "bg-emerald-500" : "bg-slate-200"}`} />)}</div>
      <button type="button" className="text-sm text-[#14263f] underline mb-3 disabled:opacity-50" disabled={busy || !f["tradingName"]} onClick={saveLead}>Save as lead &amp; finish later</button>
      {err && <p className="text-red-600 text-sm mb-2">{err}</p>}
      {step === 0 && <div>
        <h2 className="font-bold text-lg mb-3">Business details</h2>
        {Txt("tradingName", "Trading name *")}
        {Txt("legalName", "Registered company name")}
        {Txt("businessType", "Business type (sole trader / ltd …)")}
        {Txt("companyNumber", "Company number")}
        {Txt("address", "Address")}
        {Txt("postcode", "Postcode")}
        {Txt("phone", "Shop phone")}
        {Txt("town", "Town")}
        {Txt("category", "Category (takeaway, cafe …)")}
        <div className="flex gap-2"><button className={`${btn} bg-slate-200`} onClick={onCancel}>Cancel</button><button className={`${btn} bg-[#14263f] text-white`} disabled={!f["tradingName"]} onClick={() => setStep(1)}>Next</button></div>
      </div>}
      {step === 1 && <div>
        <h2 className="font-bold text-lg mb-3">Owner account</h2>
        <p className="text-sm text-slate-500 mb-3">We create a shop-owner account and email them a secure link to set their own password. You never see their password.</p>
        {Txt("ownerName", "Owner full name *")}
        {Txt("ownerEmail", "Owner email *", { type: "email", autoCapitalize: "none" })}
        {Txt("contactMobile", "Owner mobile")}
        <div className="flex gap-2"><button className={`${btn} bg-slate-200`} onClick={() => setStep(0)}>Back</button><button className={`${btn} bg-[#14263f] text-white`} disabled={!f["ownerName"] || !f["ownerEmail"]} onClick={() => setStep(2)}>Next</button></div>
      </div>}
      {step === 2 && <div>
        <h2 className="font-bold text-lg mb-3">Commercial setup</h2>
        <div className="bg-white border border-slate-200 rounded-xl p-3 text-sm text-slate-600 mb-3">
          <p>Standard terms apply: the shop receives its agreed menu price; Ayrshire Hub may apply a customer-facing uplift per approved policy. Non-standard terms need admin approval.</p>
        </div>
        <div className="mb-4"><span className="text-sm text-slate-600 block mb-2">Tablet</span>
          <div className="grid grid-cols-2 gap-2">
            <button className={`${btn} ${f["tabletProvided"] ? "bg-emerald-600 text-white" : "bg-white border border-slate-300"}`} onClick={() => set("tabletProvided", true)}>Provide Tablet (+£60)</button>
            <button className={`${btn} ${!f["tabletProvided"] ? "bg-emerald-600 text-white" : "bg-white border border-slate-300"}`} onClick={() => set("tabletProvided", false)}>Using own tablet / mobile</button>
          </div>
          {f["tabletProvided"] ? <p className="text-xs text-slate-500 mt-1">£60 will be added to the shop's account ledger.</p> : null}
        </div>
        <div className="flex gap-2"><button className={`${btn} bg-slate-200`} onClick={() => setStep(1)}>Back</button><button className={`${btn} bg-emerald-600 text-white`} disabled={busy} onClick={submit}>{busy ? "Creating…" : "Create shop"}</button></div>
      </div>}
    </div>
  );
}

function LinkCard({ label, url, colour }: { label: string; url: string; colour: string }) {
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(false);
  useEffect(() => { QRCode.toDataURL(url, { width: 220, margin: 1 }).then(setQr).catch(() => setQr(null)); }, [url]);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 mb-3">
      <div className="text-sm font-semibold mb-2">{label}</div>
      <a href={url} target="_blank" rel="noreferrer" className={`${btn} text-white inline-block text-center`} style={{ background: colour }}>Open now</a>
      <div className="flex gap-4 mt-2">
        <button className="text-xs text-slate-500 underline" onClick={() => { navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? "Copied!" : "Copy link"}</button>
        {qr && <button className="text-xs text-slate-500 underline" onClick={() => setShowQr((v) => !v)}>{showQr ? "Hide QR" : "Show QR (scan on owner's phone)"}</button>}
      </div>
      {showQr && qr && <div className="mt-2 text-center"><img src={qr} alt="QR code" className="inline-block border rounded-lg" /><div className="text-xs text-slate-500 mt-1">Owner scans this to continue privately on their own phone</div></div>}
    </div>
  );
}

function MerchantDetail({ storeId }: { storeId: number }) {
  const [data, setData] = useState<{ store: Record<string, unknown>; status: Record<string, unknown>; stripeOnboardingLink: string; legal: Record<string, string> } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const reload = useCallback(() => { void api(`/sales/merchants/${storeId}`).then(setData).catch(() => {}); }, [storeId]);
  useEffect(reload, [reload]);
  if (!data) return <p className="text-slate-500">Loading…</p>;
  const s = data.status;
  const item = (label: string, ok: boolean, detail: string) => (
    <div className="flex items-center gap-3 py-2 border-b border-slate-100">
      <span className={`w-3 h-3 rounded-full ${ok ? "bg-emerald-500" : "bg-amber-400"}`} />
      <div className="flex-1"><div className="font-semibold text-sm">{label}</div><div className="text-xs text-slate-500">{detail}</div></div>
    </div>
  );
  return (
    <div>
      <h2 className="font-bold text-xl mb-1">{String(data.store.name)}</h2>
      <div className="text-xs text-slate-500 mb-4">Stage: {String(s.stage)} · {data.store.isActive ? "LIVE" : "Not live (draft)"}</div>
      {msg && <p className="text-emerald-700 text-sm mb-2">{msg}</p>}
      <div className="bg-white border border-slate-200 rounded-xl px-3 mb-4">
        {item("Business details", true, "Captured")}
        {item("Payment setup (Stripe)", s.stripe === "complete", s.stripe === "complete" ? "Verified by Stripe" : s.stripe === "pending" ? "Started — owner to finish on their device" : "Not started")}
        {item("Merchant agreement", s.agreement === "signed", s.agreement === "signed" ? "Signed & counter-signed" : s.agreement === "awaiting_countersign" ? "Owner signed — awaiting your counter-signature" : "Awaiting owner signature")}
        {item("Menu photos", s.menu !== "none", s.menu === "none" ? "None uploaded" : String(s.menu))}
        {item("Tablet", true, s.tabletProvided ? "Provided (+£60)" : "Own device")}
        {item("Admin review", s.approved === true, s.review === "approved" ? "Approved" : "Pending")}
      </div>

      <LinkCard label="Payment setup (open / hand to owner)" url={data.stripeOnboardingLink} colour="#f97316" />

      {s.agreement === "awaiting_owner" && <OwnerSign storeId={storeId} onDone={() => { setMsg("Owner signature recorded."); reload(); }} />}
      {s.agreement === "awaiting_countersign" && <CounterSign storeId={storeId} onDone={() => { setMsg("Agreement counter-signed."); reload(); }} />}
      {s.agreement === "signed" && <AgreementPdfButton storeId={storeId} label="View signed agreement (PDF)" className="text-sm text-[#14263f] underline block mb-4" />}

      <MenuUploader storeId={storeId} onDone={() => { setMsg("Menu photos uploaded."); reload(); }} />

      <button className={`${btn} bg-emerald-600 text-white mt-4`} onClick={async () => { try { await api(`/sales/merchants/${storeId}/submit-for-review`, { method: "POST" }); setMsg("Submitted for admin review."); reload(); } catch (e) { setMsg((e as Error).message); } }}>Submit for admin review</button>
    </div>
  );
}

function OwnerSign({ storeId, onDone }: { storeId: number; onDone: () => void }) {
  const [terms, setTerms] = useState<{ title: string; content: string } | null>(null);
  const [name, setName] = useState(""); const [sig, setSig] = useState<string | null>(null); const [agree, setAgree] = useState(false); const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  useEffect(() => { void api("/shop/terms/current").then(setTerms).catch(() => {}); }, []);
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
      <div className="font-bold mb-2">Owner signs the Terms & Conditions</div>
      <p className="text-xs text-slate-600 mb-2">Hand the device to the shop owner. They should read and sign.</p>
      <div className="bg-white border rounded-lg p-2 text-xs max-h-40 overflow-auto whitespace-pre-wrap mb-2">{terms?.content || "Loading terms…"}</div>
      <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /> I am authorised to sign for this business and agree to the Terms.</label>
      <input className={`${field} mb-2`} placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
      <SignaturePad onChange={setSig} />
      {err && <p className="text-red-600 text-xs mt-1">{err}</p>}
      <button className={`${btn} bg-[#14263f] text-white mt-2`} disabled={busy || !agree || name.trim().length < 2 || !sig} onClick={async () => { setBusy(true); setErr(null); try { await api(`/sales/merchants/${storeId}/owner-sign`, { method: "POST", body: JSON.stringify({ signedName: name, signatureDataUrl: sig }) }); onDone(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); } }}>{busy ? "Saving…" : "Owner: confirm & sign"}</button>
    </div>
  );
}

function CounterSign({ storeId, onDone }: { storeId: number; onDone: () => void }) {
  const [name, setName] = useState(""); const [sig, setSig] = useState<string | null>(null); const [cap, setCap] = useState(""); const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
      <div className="font-bold mb-2">Counter-sign (Ayrshire Hub representative)</div>
      <input className={`${field} mb-2`} placeholder="Owner's capacity (e.g. Director)" value={cap} onChange={(e) => setCap(e.target.value)} />
      <input className={`${field} mb-2`} placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
      <SignaturePad onChange={setSig} />
      {err && <p className="text-red-600 text-xs mt-1">{err}</p>}
      <button className={`${btn} bg-[#14263f] text-white mt-2`} disabled={busy || name.trim().length < 2 || !sig} onClick={async () => { setBusy(true); setErr(null); try { await api(`/sales/merchants/${storeId}/countersign`, { method: "POST", body: JSON.stringify({ signedName: name, signatureDataUrl: sig, ownerCapacity: cap }) }); onDone(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); } }}>{busy ? "Saving…" : "Counter-sign & generate agreement"}</button>
    </div>
  );
}

function MenuUploader({ storeId, onDone }: { storeId: number; onDone: () => void }) {
  const [busy, setBusy] = useState(false); const [count, setCount] = useState(0); const [err, setErr] = useState<string | null>(null);
  async function uploadOne(file: File): Promise<string> {
    const req = await api("/storage/uploads/request-url", { method: "POST", body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type || "image/jpeg" }) });
    await fetch(req.uploadURL, { method: "PUT", headers: { "Content-Type": file.type || "image/jpeg", ...(token() ? { Authorization: `Bearer ${token()}` } : {}) }, body: file });
    const fin = await api("/storage/uploads/finalize", { method: "POST", body: JSON.stringify({ finalizeToken: req.finalizeToken }) });
    return fin.objectPath || req.objectPath;
  }
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4">
      <div className="font-bold mb-1">Menu photos</div>
      <p className="text-xs text-slate-500 mb-2">Photograph every page of the menu — prices, meal deals, drinks and offers.</p>
      {err && <p className="text-red-600 text-xs mb-1">{err}</p>}
      <input type="file" accept="image/*,application/pdf" capture="environment" multiple disabled={busy}
        onChange={async (e) => {
          const files = Array.from(e.target.files || []); if (!files.length) return;
          setBusy(true); setErr(null);
          try {
            const paths: Array<{ objectPath: string; kind: string }> = [];
            for (const file of files) { paths.push({ objectPath: await uploadOne(file), kind: "menu" }); setCount((c) => c + 1); }
            await api(`/sales/merchants/${storeId}/menu-photos`, { method: "POST", body: JSON.stringify({ items: paths }) });
            onDone();
          } catch (ex) { setErr((ex as Error).message); } finally { setBusy(false); }
        }} />
      {busy && <p className="text-xs text-slate-500 mt-1">Uploading… {count} done</p>}
    </div>
  );
}
